/**
 * The two reads TenantProfileLauncher opens a ticket's tenant profile from.
 *
 * They deliberately do NOT reuse `useBookingsForUser` / `useProfile`: both name
 * columns that do not exist in the schema, and PostgREST fails the WHOLE read
 * (42703) when one is missing. That is true on production, not only on a
 * lagging database:
 *   - `useBookingsForUser` embeds `rooms(room_number)`. `room_number` is a
 *     column of `bookings`; `rooms` has none (see the note in
 *     20260907170100_referral_capture_and_lookup.sql).
 *   - `useProfile` used to select `profiles.avatar_url`, which no table has
 *     (dropped in COL-548). It still selects `profiles.full_name`, which
 *     production has and the test environment lacks.
 * A failed read leaves `data` undefined, so the launcher took "could not read"
 * for "has no booking": it rendered nothing at all for every tenant (COL-514),
 * and its first fix then showed the "no booking" popup for a tenant who has
 * one (Alicia Dustin, test environment).
 *
 * The booking read is `select("*")`, like the profile dialog's own `useBooking`
 * / `useRoom` / `useProperty`: it cannot 400 on a missing column, and the dialog
 * reads that same row anyway. The launcher needs the booking row, not the
 * joined room labels the shared hook adds.
 *
 * The profile read names only the identity columns that exist on production
 * and the test environment alike. It leaves out `full_name` (missing on the
 * test environment; production keeps it in sync with `name` through
 * `trg_sync_profile_name_columns`), `avatar_url`, and anything the dialog never
 * shows (address, management fee, ...).
 *
 * Query keys are distinct from the shared hooks' on purpose: these rows have a
 * different shape (no `room_title` etc.), so they must not land in a cache
 * entry other screens read as `BookingWithRoom`.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { BookingRow } from "@/hooks/v2/useProperties";

/** Exists on production and the test environment; deliberately not `full_name` / `avatar_url`. */
export const TICKET_TENANT_PROFILE_COLUMNS =
  "id, email, name, phone, date_of_birth";

/** Newest first, so `pickTenantBooking`'s fallback is simply the first row. */
export function useTicketTenantBookings(userId: string | null) {
  return useQuery({
    queryKey: ["support", "ticket-tenant-bookings", userId],
    enabled: !!userId,
    queryFn: async (): Promise<BookingRow[]> => {
      const { data, error } = await supabase
        .from("bookings")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false });
      if (error) {
        // The launcher degrades to a popup that makes no claim about bookings;
        // keep the cause findable instead of swallowing it.
        console.error(
          "[support] could not load the ticket tenant's bookings",
          error
        );
        throw error;
      }
      return (data ?? []) as BookingRow[];
    },
  });
}

export function useTicketTenantProfile(userId: string | null) {
  return useQuery({
    queryKey: ["support", "ticket-tenant-profile", userId],
    enabled: !!userId,
    queryFn: async (): Promise<Record<string, any> | null> => {
      const { data, error } = await supabase
        .from("profiles")
        .select(TICKET_TENANT_PROFILE_COLUMNS)
        .eq("id", userId)
        .maybeSingle();
      if (error) {
        console.error(
          "[support] could not load the ticket tenant's profile",
          error
        );
        throw error;
      }
      return data ?? null;
    },
  });
}
